import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, MinusCircle, XCircle, Loader2, PiggyBank, Pencil } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const BADGE = {
  returned_full: { label: "Returned in full", cls: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]", Icon: CheckCircle2 },
  returned_partial: { label: "Partially returned", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", Icon: MinusCircle },
  withheld: { label: "Withheld — dispute available", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]", Icon: XCircle },
};

export function OutcomeBadge({ outcome, testId }) {
  const status = outcome?.status;
  if (!status || status === "pending" || !BADGE[status]) return null;
  const { label, cls, Icon } = BADGE[status];
  const text = status === "returned_partial" && outcome.amount_returned != null
    ? `Partially returned — ${money(outcome.amount_returned)}`
    : label;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${cls}`}>
      <Icon size={13} /> {text}
    </span>
  );
}

export function OutcomePrompt({ property, onUpdated }) {
  const outcome = property.outcome;
  const isSet = outcome && outcome.status && outcome.status !== "pending";
  const [editing, setEditing] = useState(!isSet);
  const [choice, setChoice] = useState(null); // returned_full | returned_partial | withheld
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (status, amt) => {
    setSaving(true);
    try {
      const body = { status };
      if (status === "returned_partial" || status === "returned_full") {
        if (amt !== undefined && amt !== "") body.amount_returned = parseFloat(amt);
      }
      const { data } = await api.patch(`/properties/${property.property_id}/outcome`, body);
      onUpdated(data.outcome);
      setEditing(false);
      setChoice(null);
      setAmount("");
      toast.success("Outcome saved. Thanks for closing the loop!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that — try again.");
    } finally {
      setSaving(false);
    }
  };

  const pick = (status) => {
    if (status === "withheld") return submit("withheld");
    setChoice(status);
    setAmount(status === "returned_full" && property.deposit_amount ? String(property.deposit_amount) : "");
  };

  // Already recorded — compact summary with a change option.
  if (isSet && !editing) {
    return (
      <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="outcome-summary">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <PiggyBank size={18} className="text-brand" />
            <span className="text-sm font-semibold text-navy">Deposit outcome</span>
            <OutcomeBadge outcome={outcome} testId="outcome-summary-badge" />
          </div>
          <Button data-testid="outcome-edit-btn" variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-[#64748B]">
            <Pencil size={14} className="mr-1.5" /> Change
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC] p-5 sm:p-6" data-testid="outcome-prompt">
      <div className="flex items-center gap-2">
        <PiggyBank size={20} className="text-brand" />
        <h2 className="font-heading text-lg font-semibold text-navy">Did you get your deposit back?</h2>
      </div>
      <p className="mt-1 text-sm text-[#64748B]">Let us know how it turned out. This stays private to you.</p>

      {!choice ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <OutcomeButton testId="outcome-btn-full" Icon={CheckCircle2} label="Returned in full" cls="border-[#BBF7D0] hover:bg-[#F0FDF4] text-[#166534]" onClick={() => pick("returned_full")} disabled={saving} />
          <OutcomeButton testId="outcome-btn-partial" Icon={MinusCircle} label="Partially returned" cls="border-[#FDE68A] hover:bg-[#FFFBEB] text-[#92400E]" onClick={() => pick("returned_partial")} disabled={saving} />
          <OutcomeButton testId="outcome-btn-withheld" Icon={XCircle} label="Withheld" cls="border-[#FECACA] hover:bg-[#FEF2F2] text-[#B91C1C]" onClick={() => pick("withheld")} disabled={saving} />
        </div>
      ) : (
        <div className="mt-4" data-testid="outcome-amount-form">
          <label className="text-sm font-medium text-navy">How much did you get back?</label>
          <div className="mt-2 flex items-center gap-2 max-w-xs">
            <span className="text-[#64748B]">$</span>
            <Input data-testid="outcome-amount-input" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="h-11 rounded-xl" autoFocus />
          </div>
          <div className="mt-4 flex gap-2">
            <Button data-testid="outcome-amount-save" onClick={() => submit(choice, amount)} disabled={saving || (choice === "returned_partial" && !(parseFloat(amount) > 0))} className="rounded-xl bg-brand hover:bg-brand-hover text-white">
              {saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : null} Save outcome
            </Button>
            <Button data-testid="outcome-amount-cancel" variant="outline" onClick={() => setChoice(null)} disabled={saving} className="rounded-xl">Back</Button>
          </div>
        </div>
      )}

      {isSet && (
        <button data-testid="outcome-cancel-edit" onClick={() => { setEditing(false); setChoice(null); }} className="mt-3 text-xs text-[#94A3B8] hover:text-[#64748B]">Cancel</button>
      )}
    </div>
  );
}

function OutcomeButton({ Icon, label, cls, onClick, disabled, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className={`flex flex-col items-center gap-2 rounded-xl border-2 bg-white px-3 py-4 text-sm font-semibold transition-colors disabled:opacity-50 ${cls}`}>
      <Icon size={22} /> {label}
    </button>
  );
}
